import type { AccessTokenView } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { useEffect, useState } from "react";

import { tokensApi } from "./tokensApi.ts";

/** Create & manage personal access tokens (API keys) scoped to your own data. */
export function TokensTab() {
  const toast = useToast();
  const [tokens, setTokens] = useState<AccessTokenView[] | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshToken, setFreshToken] = useState<string | null>(null);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://sally";

  function refresh() {
    tokensApi
      .list()
      .then((r) => setTokens(r.tokens))
      .catch(() => setTokens([]));
  }
  useEffect(refresh, []);

  async function create() {
    setBusy(true);
    try {
      const r = await tokensApi.create(label.trim() || "My token");
      setFreshToken(r.token);
      setLabel("");
      refresh();
    } catch (err) {
      toast.push({ tone: "error", message: `Failed: ${(err as Error).message}` });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    try {
      await tokensApi.revoke(id);
      refresh();
    } catch (err) {
      toast.push({ tone: "error", message: `Failed: ${(err as Error).message}` });
    }
  }

  function copy(text: string, what: string) {
    navigator.clipboard?.writeText(text).then(
      () => toast.push({ tone: "success", message: `${what} copied` }),
      () => {},
    );
  }

  const mcpUrl = `${baseUrl}/sally-mcp.mjs`;
  const mcpConfig = `{
  "mcpServers": {
    "sally": {
      "command": "node",
      "args": ["/absolute/path/to/sally-mcp.mjs"],
      "env": {
        "SALLY_BASE_URL": "${baseUrl}",
        "SALLY_TOKEN": "sal_your_token_here"
      }
    }
  }
}`;

  return (
    <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        className="card"
        style={{ border: "1px solid var(--border-soft)", borderRadius: 12, padding: 16 }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Create an access token</div>
        <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 0 }}>
          A token lets an external tool (via the Sally MCP server) read <strong>your own</strong>{" "}
          meetings, tasks, summaries and transcripts, and write back — add notes, create tasks, and
          update a task's owner, due date or status. It can never reach anyone else's data. You'll
          see the token once — store it safely.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="task-edit-input"
            style={{ flex: 1 }}
            placeholder="Label (e.g. Claude Code on my laptop)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => void create()}
            disabled={busy}
          >
            {busy ? "Creating…" : "Create token"}
          </button>
        </div>
        {freshToken && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              background: "rgba(251,188,4,.12)",
              border: "1px solid rgba(251,188,4,.6)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8a6100", marginBottom: 6 }}>
              Copy your token now — it won't be shown again:
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ flex: 1, wordBreak: "break-all", fontSize: 12.5 }}>{freshToken}</code>
              <button type="button" className="pill-btn" onClick={() => copy(freshToken, "Token")}>
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Your tokens</div>
        {tokens === null ? (
          <div className="tasks-empty">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="tasks-empty">No tokens yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tokens.map((t) => (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{t.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                    Created {new Date(t.createdAt).toLocaleDateString()}
                    {t.lastUsedAt
                      ? ` · last used ${new Date(t.lastUsedAt).toLocaleDateString()}`
                      : " · never used"}
                  </div>
                </div>
                <button
                  type="button"
                  className="pill-btn"
                  style={{ color: "var(--gc-red,#d93025)" }}
                  onClick={() => void revoke(t.id)}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="card"
        style={{ border: "1px solid var(--border-soft)", borderRadius: 12, padding: 16 }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Connect a tool (MCP)</div>
        <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 0 }}>
          The Sally MCP server is a single, zero-dependency file (no npm install needed).{" "}
          <strong>1)</strong> Download it, <strong>2)</strong> add the config below to your Claude
          Code / Cursor MCP settings — set <code>args</code> to where you saved the file and paste
          your token — then restart the tool. <strong>Google IAP is handled automatically</strong>{" "}
          as long as you're signed in with the gcloud CLI (<code>gcloud auth login</code>) — no
          manual token, and it never expires on you. See the <strong>Docs</strong> tab for details.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <a className="pill-btn primary" href={mcpUrl} download="sally-mcp.mjs">
            Download sally-mcp.mjs
          </a>
          <button
            type="button"
            className="pill-btn"
            onClick={() => copy(`curl -o sally-mcp.mjs "${mcpUrl}"`, "Download command")}
          >
            Copy curl
          </button>
        </div>
        <div style={{ position: "relative" }}>
          <pre
            style={{
              background: "var(--surface-2, #f6f8fc)",
              border: "1px solid var(--border-soft)",
              borderRadius: 8,
              padding: 12,
              fontSize: 12,
              overflowX: "auto",
            }}
          >
            {mcpConfig}
          </pre>
          <button
            type="button"
            className="pill-btn"
            style={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => copy(mcpConfig, "MCP config")}
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}
